/**
 * CP2 — Side-by-side Orchestrator score comparison panel.
 *
 * Renders up to 3 selected projects in a horizontal grid, showing
 * ip_novelty, market_feasibility, and technical_feasibility scores
 * from the /workflow/assessment/{projectId} endpoint.
 *
 * The backend returns axes in 0–10 form under `assessment.axes.{novelty,
 * feasibility, market_potential}.score`. Older mock contracts also exposed
 * flat 0–1 fractions under `scores.{ip_novelty, market_feasibility,
 * technical_feasibility}` — extractScores() tolerates both.
 *
 * Scores are displayed as labelled progress bars (0–100) with colour
 * bands: ≥70 green, 40–69 amber, <40 red.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, BarChart3, Loader2, Sparkles } from 'lucide-react'
import { getAssessment } from '../../services/api'
import type { Project } from '../../types'
import SynergyPanel from './SynergyPanel'

interface Props {
  projects: Project[]
  onClose: () => void
}

interface ScoreBarProps {
  label: string
  value: number | undefined
}

interface NormalisedScores {
  ip_novelty?: number
  market_feasibility?: number
  technical_feasibility?: number
}

function extractScores(data: unknown): NormalisedScores | undefined {
  if (!data || typeof data !== 'object') return undefined
  const d = data as {
    scores?: NormalisedScores
    assessment?: { axes?: Record<string, { score?: number }> }
  }
  // Legacy/test shape — already-flat 0–1 fractions
  if (d.scores && Object.values(d.scores).some((v) => typeof v === 'number')) {
    return d.scores
  }
  // Real backend shape — 0–10 axes, divide to get fractions for the ScoreBar
  const axes = d.assessment?.axes
  if (axes && Object.keys(axes).length > 0) {
    const toFraction = (s?: number) =>
      typeof s === 'number' ? Math.max(0, Math.min(1, s / 10)) : undefined
    return {
      ip_novelty: toFraction(axes.novelty?.score),
      market_feasibility: toFraction(axes.market_potential?.score),
      technical_feasibility: toFraction(axes.feasibility?.score),
    }
  }
  return undefined
}

function ScoreBar({ label, value }: ScoreBarProps) {
  if (value === undefined || value === null) {
    return (
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>{label}</span>
          <span className="text-gray-500">—</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full" />
      </div>
    )
  }

  const pct = Math.min(100, Math.max(0, Math.round(value * 100)))
  const colour =
    pct >= 70
      ? 'bg-emerald-500'
      : pct >= 40
      ? 'bg-amber-400'
      : 'bg-red-400'

  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        <span className="font-mono font-medium">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${colour}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  )
}

interface ProjectColumnProps {
  project: Project
}

function ProjectColumn({ project }: ProjectColumnProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['assessment', project.project_id],
    queryFn: () => getAssessment(project.project_id),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const scores = extractScores(data)

  return (
    <div
      className="flex-1 min-w-0 border-r last:border-r-0 border-gray-200 px-5"
      data-testid={`compare-column-${project.project_id}`}
    >
      <div className="mb-4">
        <h4
          className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight mb-1"
          title={project.name}
        >
          {project.name}
        </h4>
        {project.description && (
          <p className="text-xs text-gray-500 line-clamp-2">{project.description}</p>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
          <Loader2 size={14} className="animate-spin" />
          Loading scores…
        </div>
      )}

      {isError && (
        <p className="text-xs text-gray-500 py-4">
          No assessment data yet.
        </p>
      )}

      {!isLoading && !isError && (
        <>
          <ScoreBar label="Invention Novelty" value={scores?.ip_novelty} />
          <ScoreBar label="Market Feasibility" value={scores?.market_feasibility} />
          <ScoreBar label="Technical Feasibility" value={scores?.technical_feasibility} />
        </>
      )}
    </div>
  )
}

export default function CompareProjectsPanel({ projects, onClose }: Props) {
  const [showSynergies, setShowSynergies] = useState(false)

  if (projects.length === 0) return null

  return (
    <div
      className="light-card p-5 mt-4"
      data-testid="compare-projects-panel"
      role="region"
      aria-label="Project comparison"
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={16} className="text-iflow-500" />
        <h3 className="text-sm font-semibold text-gray-900">Compare Projects</h3>
        <span className="text-xs text-gray-500 ml-1">
          ({projects.length} selected)
        </span>
        {projects.length === 2 && (
          <button
            type="button"
            onClick={() => setShowSynergies((v) => !v)}
            className="ml-2 flex items-center gap-1 text-xs px-2 py-1 rounded border border-iflow-200 text-iflow-600 hover:bg-iflow-50 transition-colors"
            aria-label={showSynergies ? 'Hide synergy analysis' : 'Show synergy analysis'}
            data-testid="show-synergies-btn"
          >
            <Sparkles size={12} />
            {showSynergies ? 'Hide Synergies' : 'Show Synergies'}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto p-1 text-gray-500 hover:text-gray-600 rounded transition-colors"
          aria-label="Close comparison panel"
          data-testid="compare-panel-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex divide-x divide-gray-200 -mx-5 px-5">
        {projects.map((project) => (
          <ProjectColumn key={project.project_id} project={project} />
        ))}
      </div>

      {showSynergies && projects.length === 2 && (
        <SynergyPanel
          projectA={projects[0]}
          projectB={projects[1]}
          onClose={() => setShowSynergies(false)}
        />
      )}

      <p className="text-xs text-gray-500 mt-4 border-t border-gray-100 pt-3">
        Scores are generated by the Orchestrator from your project's documents. Run an
        assessment from the project view to populate them.
      </p>
    </div>
  )
}
